import { CellClassParams, ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useMemo } from 'react';
import { aggridHeaderClass, SmallTitleWithSubSpan } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { defaultHRM1030_R01HeaderDto, HRM1030_R01HeaderDto, HRM1030_R01HeaderGridRow } from './Dto_HRM1030_R01';
import { useHRM1030_R01Store } from './Store_HRM1030_R01';
import { formatHRM1030_R01Number, getHRM1030_R01Summary, HRM1030_R01_LAW_MIN_COUNT } from './Util_HRM1030_R01';

/** 컬럼 설정 저장과 복원에 쓰는 사원 그리드 키이다. */
const COLUMN_STATE_KEY = 'HRM1030_R01-grid-column-state';

/** 사원 목록 참조와 행 전환 함수를 받는다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM1030_R01HeaderGridRow> | null>; // 사원 그리드
    onSelect: (row: HRM1030_R01HeaderDto) => void; // 사원 선택
}

/**
 * 조회 전용 사원 목록과 교육년도 기준 교육 집계를 표시한다. 법정 이수 2건 미만은 빨간색이다.
 * @param gridRef 사원 그리드 참조
 * @param onSelect 사원 행 클릭 시 선택 사원을 바꾸는 함수. 변경이 있으면 확인창을 먼저 띄운다.
 * @returns 사원 목록 그리드 JSX
 */
export const Table_HRM1030_R01 = ({ gridRef, onSelect }: Props) => {


    // 화면 Store에서 사원 목록, 선택 사원, 선택 사원 집계 계산용 교육 목록·D 표시, 컬럼·스크롤 상태를 가져온다.
    const { HRM1030_R01List, selectedHRM1030_R01, HRM1030_R01TrainingList, deletedHRM1030_R01TrainingList,
        columnState, setColumnState, scrollTop, setScrollTop } = useHRM1030_R01Store();

    // 그리드 기본 설정: 행 ID 생성, 행·셀 CSS 규칙 생성 함수, 빈 행을 채운 표시 목록을 받는다.
    const { getRowId, createRowClassRules, createCellClassRules, paddedGridData } = useGridSettingContent<HRM1030_R01HeaderGridRow>({
        gridRef, key: 'valueIndex', gridData: HRM1030_R01List,
        defaultGridData: defaultHRM1030_R01HeaderDto, selectedRow: selectedHRM1030_R01,
    });


    // 선택 사원의 이수시간·법정 건수. 편집 중인 교육 목록으로 바로 다시 계산한다.
    const selectedSummary = useMemo(
        () => getHRM1030_R01Summary(HRM1030_R01TrainingList, deletedHRM1030_R01TrainingList),
        [HRM1030_R01TrainingList, deletedHRM1030_R01TrainingList],
    );


    // 그리드에 넘길 행 목록. 선택 사원 행의 건수·이수시간·법정만 편집 중인 값으로 바꾼다(HRM1040 공제가족과 같은 방식).
    const displayGridData = useMemo(() => paddedGridData.map(row =>
        // 삼항 연산자: 선택 사원 행이면 집계를 덮어쓴 복사본, 아니면 원래 행이다.
        selectedHRM1030_R01 && row.empNo === selectedHRM1030_R01.empNo
            ? {
                ...row,
                trainCount: HRM1030_R01TrainingList.filter(item => !deletedHRM1030_R01TrainingList.includes(String(item.valueIndex))).length,
                completeHours: selectedSummary.completeHours,
                lawCount: selectedSummary.lawCount,
            }
            : row,
    ), [paddedGridData, selectedHRM1030_R01, selectedSummary, HRM1030_R01TrainingList, deletedHRM1030_R01TrainingList]);



    /** 사원 목록 컬럼 정의이다. 모든 컬럼은 조회 전용이다. */
    const baseColumnDefs = useMemo<ColDef<HRM1030_R01HeaderGridRow>[]>(() => [
        // 공통 행 번호 컬럼 설정을 그대로 풀어 넣는다(제목 빈칸, 너비 60). 교육 이력·첨부 그리드와 같은 너비이다.
        ...getDefaultRowIndexColumnConfig(),
        { headerName: '사원번호', colId: 'empNo', field: 'empNo', width: 100 },
        { headerName: '사원명', colId: 'empNm', field: 'empNm', width: 88 },
        { headerName: '부서', colId: 'orgNm', field: 'orgNm', width: 96 },
        { headerName: '직위', colId: 'positionNm', field: 'positionNm', width: 60 },
        { headerName: '건수', colId: 'trainCount', field: 'trainCount', width: 52 },
        {
            headerName: '이수시간', colId: 'completeHours', field: 'completeHours', width: 72,
            valueFormatter: params => formatHRM1030_R01Number(params.value, 1),
        },
        {
            headerName: '법정', colId: 'lawCount', field: 'lawCount', width: 58,
            // 법정의무 이수 건수가 2건 미만인 사원은 빨간 글씨로 표시한다. 빈 행·하단 행은 제외한다.
            // AG Grid는 cellStyle이 null을 돌려주면 이전 스타일을 지우지 않는다. 선택 사원 행은 교육 이력을
            // 불러오기 전 0건으로 한 번 그려지므로, 빨간색이 아닐 때도 빈 값을 돌려 이전 글자색·굵기를 지운다.
            cellStyle: (params: CellClassParams<HRM1030_R01HeaderGridRow>) =>
                !params.node.rowPinned && params.data?.empNo && Number(params.value) < HRM1030_R01_LAW_MIN_COUNT
                    ? { color: '#dc2626', fontWeight: 600 }
                    : { color: '', fontWeight: '' },
        },
        { headerName: '재직', colId: 'empStsNm', field: 'empStsNm', width: 62 },
    ], []);



    // 기본 컬럼 순서. 의존성: 컬럼 정의가 바뀔 때만 다시 만든다.
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);
    /** 저장된 컬럼 순서·너비를 공통 컬럼 정의 함수로 복원한다(PRD2010_R01 작업지시와 같은 형태). */
    const columnDefs = useMemo(() => {
        if (columnState === null) return baseColumnDefs;
        return GridCustomColumnDefs<HRM1030_R01HeaderGridRow>({
            baseColumnDefs,
            columnState,
            headerNames: {
                rowIndex: '', empNo: '사원번호', empNm: '사원명', orgNm: '부서', positionNm: '직위',
                trainCount: '건수', completeHours: '이수시간', lawCount: '법정', empStsNm: '재직',
            },
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [baseColumnDefs, columnState]);


    /** 하단 고정 행: 사원번호 칸에 조회 사원 수, 이수시간 칸에 합계를 표시한다. */
    const pinnedBottomRow = useMemo(() => [{
        empNo: `${HRM1030_R01List.length.toLocaleString()}건`,
        completeHours: displayGridData.reduce((total, row) => total + (Number(row.completeHours) || 0), 0),
    }], [HRM1030_R01List.length, displayGridData]);


    /** 공통 셀 규칙으로 하단 고정 행의 건수를 가운데 정렬한다. 편집·포인터 컬럼이 없는 조회 전용 설정이다. */
    const cellCssDefaultColDef = {
        ...defaultColDef,
        editable: false,
        cellClassRules: createCellClassRules<HRM1030_R01HeaderGridRow>({
            idKey: 'valueIndex',
            // 삼항 연산자: 선택 사원이 있으면 그 행만 선택 표시한다.
            selectedList: selectedHRM1030_R01 ? [selectedHRM1030_R01.valueIndex] : [],
            deletedList: [],
            disabledInsertedColumns: [],
            disabledColumns: [],
            pointerInsertedColumns: [],
            pointerColumns: [],
        }),
    };

    return (
        <section className="flex flex-col h-full min-h-0">
            {/* 우클릭 메뉴와 컬럼 상태 저장을 담당하는 공통 감싸기 컴포넌트 */}
            <GridContextWrapper<HRM1030_R01HeaderGridRow>
                gridRef={gridRef}
                paddedGridData={displayGridData}
                defaultRowData={defaultHRM1030_R01HeaderDto}
                baseColumnDefs={columnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setColumnState}
                localStorageKey={COLUMN_STATE_KEY}>
                {/* 공통 영역 제목(파란 막대 + 제목). 오른쪽에 빨간색 기준을 안내한다. */}
                <SmallTitleWithSubSpan title="사원 목록" subTitle={`법정 이수 ${HRM1030_R01_LAW_MIN_COUNT}건 미만 빨간색`} />
                {/* 사원 목록 그리드: 편집 없이 선택 행 표시, 행 클릭 선택, 하단 건수·합계 행을 연결한다. */}
                <Grid_Excel<HRM1030_R01HeaderGridRow>
                    ref={gridRef}
                    rowData={displayGridData}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    defaultColDef={cellCssDefaultColDef}
                    rowClassRules={createRowClassRules<HRM1030_R01HeaderGridRow>({
                        idKey: 'valueIndex',
                        selectedList: selectedHRM1030_R01 ? [selectedHRM1030_R01.valueIndex] : [],
                        deletedList: [],
                    })}
                    onRowClicked={event => {
                        // 하단 고정 행과 사원번호가 없는 빈 행은 선택하지 않는다.
                        if (!event.node.rowPinned && event.data?.empNo) onSelect(event.data);
                    }}
                    pinnedBottomRowData={pinnedBottomRow}
                    columnStateKey={COLUMN_STATE_KEY}
                    setColumnState={setColumnState}
                    getDefaultColumnState={() => defaultColumnState}
                    scrollTop={scrollTop}
                    setScrollTop={setScrollTop} />
            </GridContextWrapper>
        </section>
    );
};
